/**
 * PtPlansManage — standalone "PT Plans" (per-gym) management page.
 *
 * Shows ALL gyms' PT plans in one table (search + gym filter). Per row:
 *   - Edit   : open the PT form pre-filled with THIS gym's actual sessions/prices
 *              (submits to /addon/web/pt/update — member-safe session guard).
 *   - Delete : soft-delete the gym's PT addon (/addon/delete) — members' existing
 *              subscriptions stay intact.
 *   - Add PT : apply a master PT plan to a chosen gym (/addon/web/pt).
 *
 * Fully separate from the Centralised Plan -> Addons tab (own copied form).
 */

import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import HelmetWrapper from "../../HelmetWrapper";
import Loader from "../../components/ui/Loader";
import CustomTable from "../../components/components-bootstrap/CustomTable";
import { selectToken } from "../../components/auth/auth.selector";
import { selectGymData } from "../../redux/reducers/gymDataSlice";
import { fetchGymData } from "../../data/APIs";
import { useGetFetch, useUpdateFetch } from "../../data/Queries";
import {
  addonCategories,
  addonCategoryEndPoint,
  getAddonEndpoint,
  deleteAddonEndpoint,
  sessionAddonGet,
  sessionAddonGetKey,
} from "../../data/ApiEndpoints";
import { fetchPtPlanDraftsData } from "../../redux/plans-reducers/ptPlansSlice";

const PtPlanForm = lazy(() => import("../../components/pt-plans/PtPlanForm"));

const CloseX = ({ onClick }) => (
  <button className="btn-k small btn-muted-k icon-close-k rounded-full-k" onClick={onClick} aria-label="Close">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
    </svg>
  </button>
);
const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z" />
  </svg>
);
const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5" />
  </svg>
);

const PtPlansManage = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const token = useSelector(selectToken);
  const gymData = useSelector(selectGymData);
  const { ptDrafts } = useSelector((state) => state.ptPlans);

  const [search, setSearch] = useState("");
  const [gymFilter, setGymFilter] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentAddon, setCurrentAddon] = useState(null);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [addGym, setAddGym] = useState(null);

  useEffect(() => {
    dispatch(fetchGymData(token, 1, 500, "", "active"));
    dispatch(fetchPtPlanDraftsData({ filters: { status: "active" }, token }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: addonCategoriesData } = useGetFetch({
    url: addonCategoryEndPoint,
    filters: { status: "active" },
    token,
    key: addonCategories,
  });

  // All mapped PT addons across gyms
  const {
    data: allAddonsData,
    isLoading: isLoadingAddons,
    isFetching: isFetchingAddons,
    refetch: refetchAddons,
  } = useGetFetch({
    key: "ptPlansAllAddons",
    url: getAddonEndpoint,
    filters: { is_web: 1, status: "active", limit: 500 },
    token,
  });

  const { mutateAsync: deleteAddon } = useUpdateFetch({
    url: deleteAddonEndpoint,
    key: "ptPlansAllAddons",
    token,
  });

  // Edit prefill: the gym addon's ACTUAL sessions + selling prices. Key includes
  // the addon uid so react-query refetches per addon (not a stale cache).
  const { data: editSessionsData, isLoading: isLoadingEditSessions } =
    useGetFetch({
      key: [sessionAddonGetKey, currentAddon?.uid || "none"],
      url: sessionAddonGet,
      filters: { addon: currentAddon?.uid || "__none__" },
      token,
    });

  const gymNameById = useMemo(() => {
    const map = {};
    (gymData || []).forEach((g) => {
      if (g?.value) map[g.value] = g.label;
    });
    return map;
  }, [gymData]);

  // Only PT addons
  const ptAddons = useMemo(() => {
    const rows = allAddonsData?.data || [];
    return rows
      .filter((a) => `${a?.is_pt}` === "1" && a?.status !== "deleted")
      .map((a) => ({ ...a, gym_name: a?.gym_name || gymNameById[a?.gym_id] || a?.gym_id }));
  }, [allAddonsData, gymNameById]);

  // For the Add flow: only gyms that DON'T already have a PT plan (gyms with one
  // should use Edit). Avoids confusing "add" on an already-configured gym.
  const gymsWithoutPt = useMemo(() => {
    const taken = new Set(ptAddons.map((a) => a?.gym_id));
    return (gymData || []).filter((g) => !taken.has(g?.value));
  }, [gymData, ptAddons]);

  // Apply search + gym filter
  const filteredRows = useMemo(() => {
    let rows = ptAddons;
    if (gymFilter?.value) rows = rows.filter((a) => a?.gym_id === gymFilter.value);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (a) =>
          `${a?.gym_name || ""}`.toLowerCase().includes(q) ||
          `${a?.name || ""}`.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [ptAddons, gymFilter, search]);

  const masterOptions = useMemo(() => {
    const rows = ptDrafts?.data || ptDrafts || [];
    return (Array.isArray(rows) ? rows : []).map((d) => ({
      label: d?.addon_name || d?.name || "PT Plan",
      value: d?.uid,
      ...d,
    }));
  }, [ptDrafts]);

  function getDraftForAddon(addon) {
    const rows = ptDrafts?.data || ptDrafts || [];
    return (Array.isArray(rows) ? rows : []).find((d) => d?.uid === addon?.draft_uid);
  }

  const editDraft = currentAddon ? getDraftForAddon(currentAddon) : null;

  const editSessionDetails = useMemo(() => {
    const rows = editSessionsData?.data || [];
    return rows.map((r) => ({
      num_session: r?.n_session,
      duration: r?.duration,
      base_price: r?.price,
      is_trial: `${r?.is_trial}` === "1",
      session_key: r?.session_key,
      trainer_exp_lvl: r?.trainer_exp_lvl,
      tax_percentage: "",
      final_base_price: "",
      selling_prices:
        Array.isArray(r?.price_details) && r.price_details.length
          ? r.price_details.map((pp) => ({
              final_selling_price: pp?.plan_price,
              tax_percentage: "",
              base_price: "",
            }))
          : [{ final_selling_price: "", tax_percentage: "", base_price: "" }],
    }));
  }, [editSessionsData]);

  const editIntitailData = useMemo(() => {
    if (!editDraft) return null;
    return {
      ...editDraft,
      addon_name: currentAddon?.name || editDraft?.addon_name,
      addon_for: currentAddon?.addon_for ?? editDraft?.addon_for,
      addon_days: currentAddon?.addon_days ?? editDraft?.addon_days,
      description: currentAddon?.description ?? editDraft?.description,
      intensity: currentAddon?.intensity ?? editDraft?.intensity,
      session_details: editSessionDetails,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDraft, currentAddon, editSessionDetails]);

  function closeAll() {
    setIsAddOpen(false);
    setIsEditOpen(false);
    setCurrentAddon(null);
    setAddGym(null);
    refetchAddons();
    // After an edit, drop cached sessions so the next Edit shows fresh values.
    queryClient.invalidateQueries(sessionAddonGetKey);
  }
  function onClickEdit(addon) {
    setCurrentAddon(addon);
    // Drop any stale cached sessions so this gym's CURRENT values are fetched.
    queryClient.invalidateQueries(sessionAddonGetKey);
    setIsEditOpen(true);
  }
  function onClickAdd() {
    if (!masterOptions.length) {
      toast.error("No master PT plan found. Create a PT plan first.");
      return;
    }
    setSelectedMaster(masterOptions[0]);
    setAddGym(null);
    setIsAddOpen(true);
  }
  async function onClickDelete(addon) {
    try {
      if (
        window.confirm(
          "Delete this gym's PT plan? Existing PT memberships stay intact; the plan just stops being offered for new purchases."
        )
      ) {
        const { status, message } = await deleteAddon({ data: { uid: addon?.uid } });
        if (status) {
          toast.success(message || "Deleted");
          refetchAddons();
        } else {
          toast.error(message || "Failed to delete");
        }
      }
    } catch (e) {
      console.log("PT delete error:", e);
    }
  }

  const COLS = [
    {
      dataField: "gym_name",
      text: "Gym",
      formatter: (_, row) => (
        <span className="flex-k py-2 fs-normal-k flex-center">{row?.gym_name || "N/A"}</span>
      ),
    },
    {
      dataField: "name",
      text: "PT Plan",
      formatter: (_, row) => (
        <span className="flex-k py-2 fs-normal-k flex-center">
          {row?.name || "Personal Training"}
        </span>
      ),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (_, row) => {
        const isActive = (row?.status || "active") === "active";
        return (
          <div className="flex-k flex-center">
            <span className={`badge text-capitalize fs-6 ${isActive ? "bg-success" : "bg-danger"}`}>
              {row?.status || "active"}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "action",
      text: "Action",
      formatter: (_, row) => (
        <div className="flex-k gap-05-k flex-center">
          <button onClick={() => onClickEdit(row)} className="btn-k btn-muted-k dark-text small" title="Edit">
            <PencilIcon />
          </button>
          <button onClick={() => onClickDelete(row)} className="btn-k btn-muted-k dark-text small" title="Delete">
            <TrashIcon />
          </button>
        </div>
      ),
    },
  ];

  const showLoader = isLoadingAddons || isFetchingAddons;

  return (
    <HelmetWrapper title="WTF: PT Plans">
      <div className="px-50px py-3" style={{ background: "#f7f8fa", minHeight: "100vh" }}>
        <h1 style={{ fontWeight: 700, marginBottom: 6 }}>PT Plans (Per Gym)</h1>
        <p className="text-muted" style={{ marginBottom: 18 }}>
          All gyms' Personal Training plans. Search or filter by gym, then edit, delete or add.
        </p>

        {/* Toolbar: Add | Search + Gym filter */}
        <div className="flex-k js-between-k items-end-k gap-3 p-1 my-2">
          <button className="btn-k btn-primary-k" onClick={onClickAdd}>
            + Add PT
          </button>
          <div className="flex-k items-end-k gap-2">
            <div className="flex-k flex-col-k">
              <label className="dark-text">Search</label>
              <input
                className="input-k"
                type="text"
                placeholder="Search by gym or plan name.."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex-k flex-col-k" style={{ minWidth: 240 }}>
              <label className="dark-text">Gym</label>
              <Select
                classNamePrefix="input-k-select"
                placeholder="All gyms"
                isClearable
                options={gymData || []}
                value={gymFilter}
                onChange={(opt) => setGymFilter(opt)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {showLoader ? (
          <div className="my-5 py-5">
            <Loader />
          </div>
        ) : (
          <div className="grid-k gap-3 my-3">
            <CustomTable data={filteredRows} columns={COLS} />
          </div>
        )}

        {/* Add PT modal */}
        <Modal ariaHideApp={false} isOpen={isAddOpen} onRequestClose={closeAll}>
          <div className="modal-body-k bg-white rounded-5">
            <div className="flex-k js-between-k">
              <h1>Add PT Plan{addGym ? ` — ${addGym.label}` : ""}</h1>
              <CloseX onClick={closeAll} />
            </div>
            <div className="grid-k grid-col-2-k gap-2 my-2" style={{ maxWidth: 720 }}>
              <div>
                <label>Gym *</label>
                <Select
                  classNamePrefix="input-k-select"
                  placeholder="Select a gym"
                  isClearable
                  options={gymsWithoutPt}
                  value={addGym}
                  onChange={(opt) => setAddGym(opt)}
                />
              </div>
              {masterOptions.length > 1 && (
                <div>
                  <label>Master PT Plan *</label>
                  <Select
                    classNamePrefix="input-k-select"
                    options={masterOptions}
                    value={selectedMaster}
                    onChange={(opt) => setSelectedMaster(opt)}
                  />
                </div>
              )}
            </div>
            <Suspense fallback={<Loader />}>
              {addGym && selectedMaster ? (
                <PtPlanForm
                  isMapAddon
                  singleGym={addGym}
                  intitailData={selectedMaster}
                  addonCategoriesData={addonCategoriesData}
                  onClose={closeAll}
                  refetch={refetchAddons}
                  mapCreateUrl="/addon/web/pt"
                  mapUpdateUrl="/addon/web/pt/update"
                  gymCategoryId={selectedMaster?.gym_category_id}
                  restrictToDraftUid={selectedMaster?.uid}
                />
              ) : (
                <p className="text-muted my-3">Select a gym to configure its PT plan.</p>
              )}
            </Suspense>
          </div>
        </Modal>

        {/* Edit PT modal */}
        <Modal ariaHideApp={false} isOpen={isEditOpen} onRequestClose={closeAll}>
          <div className="modal-body-k bg-white rounded-5">
            <div className="flex-k js-between-k">
              <h1>Edit PT Plan — {currentAddon?.gym_name}</h1>
              <CloseX onClick={closeAll} />
            </div>
            <Suspense fallback={<Loader />}>
              {isLoadingEditSessions ? (
                <Loader />
              ) : currentAddon && editIntitailData ? (
                <PtPlanForm
                  isMapAddon
                  singleGym={{ label: currentAddon?.gym_name, value: currentAddon?.gym_id }}
                  existingAddon={{
                    label: currentAddon?.name,
                    value: currentAddon?.uid,
                    gym_id: currentAddon?.gym_id,
                  }}
                  intitailData={editIntitailData}
                  addonCategoriesData={addonCategoriesData}
                  onClose={closeAll}
                  refetch={refetchAddons}
                  mapCreateUrl="/addon/web/pt"
                  mapUpdateUrl="/addon/web/pt/update"
                  gymCategoryId={editDraft?.gym_category_id}
                  restrictToDraftUid={editDraft?.uid}
                />
              ) : (
                <p className="my-3">
                  Could not find the master PT plan for this gym's addon. Edit is
                  unavailable; you can still Delete it.
                </p>
              )}
            </Suspense>
          </div>
        </Modal>
      </div>
    </HelmetWrapper>
  );
};

export default PtPlansManage;
